import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sprout, ShieldAlert, Orbit, Globe, ArrowRight, Download, CheckCircle2, AlertTriangle, Cpu, FileText, Sparkles, Calendar, MapPin, Activity, Layers, X, ShieldCheck } from 'lucide-react';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import { Language } from '@/types';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import { useNavigate } from 'react-router-dom';

interface CropLensPageProps {
  lang: Language;
}

const CropLensPage: React.FC<CropLensPageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  const t = {
    en: {
      badge: "GeoAI AgriTech Layer",
      title: "VEXL CropLens",
      tagline: "Satellite Crop Verification &\nInstant Claim Auditing.",
      subtitle: "Verify agricultural damages in real-time. VEXL CropLens contacts Sentinel-2 satellites to compute NDVI statistics, validate crop calendars, detect fraud risk, and compile certified audit reports.",
      ctaWorkspace: "Open CropLens Workspace",
      ctaSecondary: "Explore GeoAI Engine",
      trust: "Enterprise-grade insurance verification for Egypt and the Nile Delta.",
      problems: {
        title: "The Agricultural Claim Bottleneck",
        subtitle: "Traditional crop auditing is slow, expensive, and vulnerable to fraud",
        items: [
          { label: "Weeks of waiting for physical adjuster field visits", icon: <X className="text-red-500" /> },
          { label: "Subjective, non-scientific damage estimations", icon: <X className="text-red-500" /> },
          { label: "High operation costs for remote rural areas", icon: <X className="text-red-500" /> },
          { label: "Fraudulent claim inflation without satellite proof", icon: <X className="text-red-500" /> }
        ],
        solution: "VEXL CropLens Automates Verification."
      },
      features: [
        {
          title: "Satellite Digitizer (GPS Polygons)",
          desc: "Farmers trace crop field boundaries directly on high-resolution ESRI satellite maps. The portal dynamically calculates farm acreage in real-time using Turf.js.",
          icon: <MapPin className="text-emerald-400" size={24} />
        },
        {
          title: "Zonal Spectral NDVI Analytics",
          desc: "Our engine queries AWS S3 to fetch Red (B04) and NIR (B08) bands. It calculates pixel-level Normalized Difference Vegetation Index (NDVI) before and after the claim event.",
          icon: <Layers className="text-cyan-400" size={24} />
        },
        {
          title: "Egyptian Crop Calendar Guard",
          desc: "Cross-checks crop declarations against Egyptian peak growing seasons (e.g. Wheat from Nov-May, Cotton from Apr-Oct) to confirm planting authenticity.",
          icon: <Calendar className="text-emerald-400" size={24} />
        },
        {
          title: "AI Fraud Anomalies Indexing",
          desc: "Instantly flags high, medium, and low fraud risk states by measuring discrepancy deltas between claims and satellite-calculated loss parameters.",
          icon: <ShieldAlert className="text-cyan-400" size={24} />
        }
      ],
      specifications: {
        title: "Technical Infrastructure",
        desc: "CropLens coordinates cloud computing, spatial databases, and multi-spectral sensors.",
        items: [
          { name: "Satellite Constellation", value: "Sentinel-2 (ESA)" },
          { name: "Spatial Resolution", value: "10-Meter Band Grid" },
          { name: "Band Feeds", value: "B04 (Red), B08 (NIR), SCL (Scene Classification)" },
          { name: "Cloud Masking", value: "Zonal Z-Masking (SCL Classes 3, 8, 9, 10)" },
          { name: "Database Layout", value: "PostgreSQL + PostGIS Geometry indices" },
          { name: "PDF Output Engine", value: "Natively Compiled ReportLab Engine" }
        ]
      },
      howItWorks: {
        title: "How It Works",
        steps: [
          { step: "01", title: "Trace Field Boundary", desc: "Farmer or adjuster draws the crop boundary on the interactive map." },
          { step: "02", title: "COG Band Querying", desc: "FastAPI triggers Sentinel-2 STAC queries and reads sub-region arrays via S3 /vsicurl/." },
          { step: "03", title: "GeoAI Analysis", desc: "Calculates NDVI changes, runs calendar validation, and audits fraud probability." },
          { step: "04", title: "Download PDF", desc: "ReportLab compiles a certified PDF document with embedded spectral heatmaps." }
        ]
      },
      faq: {
        title: "Frequently Asked Questions",
        subtitle: "Quick answers to help you understand VEXL CropLens satellite-powered auditing.",
        items: [
          {
            q: "What is VEXL CropLens?",
            a: "VEXL CropLens is an agricultural GeoAI portal developed by VEXL, the parent Spatial AI organization. It helps insurance companies, banks, and farmers verify crop damage in real-time using Sentinel-2 multi-spectral satellite imagery and machine learning analytics."
          },
          {
            q: "How does Sentinel-2 crop damage verification work?",
            a: "When a field boundary is drawn, CropLens queries satellite imagery to calculate NDVI (Normalized Difference Vegetation Index) deltas before and after the claimed event. It measures crop density and health changes to calculate actual damage scientific indexes."
          },
          {
            q: "How does CropLens prevent agricultural claim fraud?",
            a: "CropLens cross-checks declarations against official Egyptian regional crop calendars (e.g. wheat growing seasons, cotton peak months) and compares farmer-claimed damage estimates with actual satellite vegetation index drops to highlight fraud probability scores."
          }
        ]
      }
    },
    ar: {
      badge: "طبقة الذكاء الاصطناعي الجغرافي للمحاصيل",
      title: "فكسل CropLens",
      tagline: "التدقيق الجغرافي للمحاصيل\nوكشف الاحتيال الزراعي فورياً.",
      subtitle: "تحقق من خسائر الأراضي الزراعية في دقائق معدودة. يتصل محرك CropLens بأقمار Sentinel-2 الصناعية لحساب مؤشرات غطاء النبات طيفياً (NDVI)، ومطابقة التقاويم الزراعية، وإصدار تقارير تدقيق موثقة.",
      ctaWorkspace: "دخول مساحة عمل CropLens",
      ctaSecondary: "استكشف المحرك الجغرافي",
      trust: "حل تأميني متكامل للمؤسسات والمزارعين في دلتا النيل ومحافظات مصر.",
      problems: {
        title: "عقبات التدقيق الزراعي التقليدي",
        subtitle: "الأساليب الميدانية بطيئة، مكلفة، وغير دقيقة علمياً",
        items: [
          { label: "انتظار لأسابيع لزيارة خبير فحص المعاينة الميدانية", icon: <X className="text-red-500" /> },
          { label: "تقديرات تقديرية ذاتية لنسب التضرر دون سند علمي", icon: <X className="text-red-500" /> },
          { label: "تكاليف تشغيلية باهظة في المناطق الريفية والنائية", icon: <X className="text-red-500" /> },
          { label: "مطالبات مالية مبالغ فيها وصعوبة كشف الاحتيال بدون إثبات", icon: <X className="text-red-500" /> }
        ],
        solution: "CropLens يقوم بأتمتة الفحص بالكامل طيفياً."
      },
      features: [
        {
          title: "مخطط الحقول الرقمي (GPS Polygons)",
          desc: "يرسم المزارع حدود حقوله مباشرة على الخرائط الطيفية للأقمار الصناعية (Esri)، لتقوم المنصة بحساب مساحة الأرض بالفدان فورياً بدقة متناهية.",
          icon: <MapPin className="text-emerald-400" size={24} />
        },
        {
          title: "تحليلات الـ NDVI الطيفية للمناطق",
          desc: "يفتح محركنا قنوات افتراضية مع مخازن AWS S3 لقراءة حزم الضوء الأحمر والأشعة تحت الحمراء، لحساب مؤشر الغطاء النباتي (NDVI) قبل الكارثة وبعدها.",
          icon: <Layers className="text-cyan-400" size={24} />
        },
        {
          title: "حارس التقويم الزراعي المصري",
          desc: "مطابقة مطالبات المحاصيل مع مواسم نموها الرسمية بمصر (مثل القمح من نوفمبر لمايو، والقطن من أبريل لأكتوبر) للتأكد من زراعة الحقل.",
          icon: <Calendar className="text-emerald-400" size={24} />
        },
        {
          title: "مؤشر كشف الاحتيال التلقائي",
          desc: "يكتشف الاختلافات بين النسبة المزعومة للمزارع والنسبة الطيفية المرصودة من القمر الصناعي، ويصنفها لثلاث مستويات خطورة.",
          icon: <ShieldAlert className="text-cyan-400" size={24} />
        }
      ],
      specifications: {
        title: "البنية التحتية والتقنية للمحرك",
        desc: "ينسق محرك CropLens العمليات السحابية، القواعد المكانية، وحسابات الاستشعار عن بعد.",
        items: [
          { name: "كوكبة الأقمار", value: "Sentinel-2 (وكالة الفضاء الأوروبية)" },
          { name: "الدقة المكانية", value: "مصفوفة بكسل بدقة 10 أمتار" },
          { name: "نطاقات الضوء Feed", value: "B04 (أحمر)، B08 (NIR)، SCL (تصنيف المشهد)" },
          { name: "تصفية الغيوم", value: "قناع طيفي ذكي لاستبعاد الغيوم والظلال" },
          { name: "هيكل البيانات", value: "قاعدة بيانات PostgreSQL مع فهارس PostGIS الجغرافية" },
          { name: "محرك تقارير الـ PDF", value: "مترجم محلي بالكامل لتقارير الـ PDF عبر ReportLab" }
        ]
      },
      howItWorks: {
        title: "كيف تعمل المنصة؟",
        steps: [
          { step: "01", title: "رسم حدود الحقل", desc: "يقوم المزارع برسم حدود أرضه جغرافياً بدقة على خريطة القمر الصناعي." },
          { step: "02", title: "استعلام طيفي سريع", desc: "يستعلم السيرفر عن إحداثيات المشهد ويقتطع مصفوفة البكسل الخاصة بالحقل في ثوانٍ." },
          { step: "03", title: "تحليل الذكاء الاصطناعي", desc: "يحسب المحرك الفروقات الطيفية ومطابقة التقاويم الزراعية وتحديد مخاطر الاحتيال." },
          { step: "04", title: "تصدير تقرير موثق", desc: "يولد المحرك تقرير PDF رسمي يحتوي على إحصائيات التضرر والخرائط الحرارية للـ NDVI." }
        ]
      },
      faq: {
        title: "الأسئلة الشائعة",
        subtitle: "إجابات سريعة لمساعدتك في فهم نظام الفحص الطيفي للمحاصيل من فكسل.",
        items: [
          {
            q: "ما هو نظام فكسل CropLens؟",
            a: "فكسل CropLens هي منصة ذكاء اصطناعي جغرافي زراعية طورتها VEXL (الشركة الأم لحلول الذكاء الجغرافي والاستشعار عن بعد). تساعد المنصة شركات التأمين والبنوك والمزارعين على التحقق الفوري من خسائر المحاصيل باستخدام أقمار Sentinel-2 والتحليلات الطيفية."
          },
          {
            q: "كيف يعمل الفحص طيفياً باستخدام أقمار Sentinel-2؟",
            a: "بمجرد رسم حدود الأرض، يستعلم النظام من كوكبة الأقمار الصناعية لحساب مؤشرات غطاء النبات طيفياً (NDVI) قبل الكارثة المزعومة وبعدها للتحقق من تغير كثافة وصحة النبات بدقة علمية."
          },
          {
            q: "كيف يمنع CropLens الاحتيال في مطالبات التعويض الزراعي؟",
            a: "يطابق النظام المحاصيل المصرح بها مع مواعيد الزراعة والنمو الرسمية بالجمهورية (مثل القمح والقطن)، ويقارن نسبة التلف المزعومة ميدانياً مع الهبوط الطيفي الفعلي للنبات طيفياً لتحديد مؤشر دقة ومصداقية المطالبة."
          }
        ]
      }
    }
  }[lang];

  const cropLensSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "@id": "https://vexl-gis.tech/products/croplens#application",
        "name": isAr ? "فكسل CropLens" : "VEXL CropLens",
        "applicationCategory": "BusinessApplication, AnalyticalApplication",
        "operatingSystem": "Web",
        "description": isAr
          ? "فكسل CropLens هو محرك تدقيق طيفي للمحاصيل وكشف الاحتيال الزراعي فورياً باستخدام الأقمار الصناعية، تم تطويره بواسطة شركة VEXL الشريكة في حلول الذكاء الاصطناعي الجغرافي."
          : "VEXL CropLens is a satellite-powered crop verification and claim auditing engine developed by VEXL. It connects with Sentinel-2 sensors to compute NDVI agricultural damage metrics and detect fraud risk.",
        "publisher": {
          "@type": "Organization",
          "name": "VEXL Studio",
          "url": "https://vexl-gis.tech",
          "logo": "https://vexl-gis.tech/logo.png",
          "founder": {
            "@type": "Person",
            "name": "Diaa Shousha",
            "jobTitle": "Principal GeoAI Architect"
          }
        },
        "featureList": isAr
          ? [
              "مخطط الحقول الرقمي وحساب المساحات بالفدان",
              "تحليلات الـ NDVI الطيفية للمناطق المتضررة",
              "حارس التقويم الزراعي المصري للتحقق من المحاصيل",
              "مؤشر كشف الاحتيال التلقائي وإصدار تقارير PDF معتمدة"
            ]
          : [
              "GPS Polygon Field Digitizer & Acreage Calculation",
              "Zonal Spectral NDVI Analytics & Change Detection",
              "Egyptian Crop Calendar Guard for validation",
              "AI Fraud Anomalies Indexing & Certified PDF Reports"
            ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": t.faq.items.map((item: any) => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.a
          }
        }))
      }
    ]
  };

  return (
    <PageTransition>
      <SEO 
        title={isAr ? 'فكسل CropLens — التدقيق الجغرافي للمحاصيل' : 'VEXL CropLens — GeoAI Crop Verification'} 
        description="Satellite-powered agricultural audit portal for instant crop loss verification and fraud prevention using Sentinel-2 and NDVI zonal statistics." 
        lang={lang}
        schema={cropLensSchema}
      />

      <div className="bg-[#05070A] min-h-screen text-[#EDF0F1] overflow-x-hidden selection:bg-[#0E93A3]/30 selection:text-white relative">
        <RasterGridBackdrop type="type-a" />

        {/* HERO SECTION */}
        <section className="pt-28 pb-20 px-6 relative z-10 border-b border-[#262C31]">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Text details */}
            <div className={`lg:col-span-7 space-y-6 ${isAr ? 'text-right lg:order-2' : 'text-left'}`}>
              <div>
                <span className="inline-flex items-center gap-2 font-mono text-[11px] text-[#639922] uppercase tracking-[0.18em]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#639922] animate-pulse" />
                  {t.badge} · Sentinel-2 COG
                </span>
              </div>

              <h1 
                className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {t.title} — {t.tagline.replace('\n', ' ')}
              </h1>

              <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-xl font-normal">
                {t.subtitle}
              </p>

              <div className={`flex flex-wrap gap-4 pt-2 ${isAr ? 'justify-end' : ''}`}>
                <button
                  onClick={() => navigate('/products/croplens/workspace')}
                  className="px-6 py-3 bg-[#0E93A3] text-white font-medium rounded-full text-xs font-mono uppercase tracking-wider flex items-center gap-2 hover:bg-[#075C68] transition-all shadow-[0_0_15px_rgba(14,147,163,0.3)]"
                >
                  <span>{t.ctaWorkspace}</span>
                  <ArrowRight size={14} className={isAr ? 'rotate-180' : ''} />
                </button>
                <button
                  onClick={() => {
                    const section = document.getElementById('engine-details');
                    section?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-6 py-3 bg-[#12161A] text-[#EDF0F1] border border-[#262C31] hover:border-[#0E93A3]/50 font-medium rounded-full text-xs font-mono uppercase tracking-wider transition-all"
                >
                  {t.ctaSecondary}
                </button>
              </div>

              {/* Trust badges */}
              <div className={`flex items-center gap-2.5 text-[11px] font-mono text-[#66707A] uppercase tracking-widest pt-2 ${isAr ? 'justify-end' : ''}`}>
                <Globe size={13} className="text-[#639922]" />
                <span>{t.trust}</span>
              </div>
            </div>

            {/* Right Column (lg:col-span-5) — Type A: NDVI Spectral Map Visual */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="w-full rounded-2xl border border-[#262C31] bg-[#0E1217] overflow-hidden shadow-2xl">
                {/* Modern Window Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-[#12161A] border-b border-[#262C31] text-xs font-mono">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                    <span className="text-[#9AA5AC] pl-2 border-l border-[#262C31] text-[11px]">
                      CropLens · Zonal Verification
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[#639922] text-[11px]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#639922] animate-pulse" />
                    <span>Sentinel-2 COG</span>
                  </div>
                </div>

                {/* Raster Visual Frame */}
                <div className="relative aspect-[4/3] bg-[#05070A] overflow-hidden">
                  <img
                    src="/images/products/croplens.webp"
                    alt="VEXL CropLens Sentinel-2 NDVI Verification"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* HOW IT WORKS SECTION */}
        <section className="py-24 px-6 border-t border-white/5 bg-slate-950/20 relative z-10">
          <div className="max-w-6xl mx-auto">
            <h2 className={`text-2xl md:text-4xl font-bold tracking-tight text-center mb-16 ${isAr ? 'font-arabic' : ''}`}>
              {t.howItWorks.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {t.howItWorks.steps.map((step, idx) => (
                <div key={idx} className="p-6 liquid-glass-interactive relative space-y-4 hover:border-emerald-500/20">
                  <div className="text-4xl font-black font-mono text-emerald-500/20">{step.step}</div>
                  <h3 className="text-base font-bold text-white">{step.title}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed font-medium">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROBLEM / SOLUTION COMPARISON */}
        <section className="py-32 bg-[#050608]/50 relative overflow-hidden border-y border-white/[0.02] z-10">
          <div className="max-w-5xl mx-auto px-6 relative z-10">
            <div className="text-center mb-20 space-y-4">
              <div className="flex justify-center">
                <span className="text-[10px] font-mono text-emerald-400 tracking-[0.2em] uppercase bg-emerald-500/10 px-3.5 py-1 rounded-full border border-emerald-500/20">
                  {isAr ? 'التحديات والحلول الجغرافية' : 'Audit Inefficiency & Solution'}
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight text-white">
                {t.problems.title}
              </h2>
              <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto font-medium">{t.problems.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {t.problems.items.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: idx * 0.1, duration: 0.6, ease: "easeOut" }}
                  className="p-8 rounded-3xl liquid-glass-subtle flex items-center gap-6 group hover:border-red-500/20 transition-all shadow-[0_4px_20px_rgba(0,0,0,0.2)]"
                >
                  <div className="w-11 h-11 rounded-2xl bg-red-500/5 flex items-center justify-center group-hover:bg-red-500/10 transition-all">
                    {item.icon}
                  </div>
                  <span className="text-sm md:text-base font-bold text-slate-300">{item.label}</span>
                </motion.div>
              ))}
            </div>

            <div className="mt-24 text-center">
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 30 }}
                whileInView={{ scale: 1, opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="inline-flex flex-col items-center gap-8"
              >
                <div className="w-px h-16 bg-gradient-to-b from-transparent via-emerald-500/50 to-transparent" />
                <h3 className="text-3xl md:text-5xl font-bold tracking-tight text-emerald-400 mb-4 animate-pulse">
                  {t.problems.solution}
                </h3>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FEATURES DETAIL BENTO GRID */}
        <section id="engine-details" className="py-32 px-6 relative z-10">
          <div className="max-w-6xl mx-auto space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight">GeoAI Satellite Auditing Features</h2>
              <p className="text-slate-400 text-sm md:text-base max-w-2xl mx-auto font-medium">Explore the scientific methodologies driving VEXL CropLens.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {t.features.map((feat, idx) => (
                <div 
                  key={idx} 
                  className={`p-6 rounded-3xl transition-all duration-300 space-y-4 cursor-pointer hover:scale-[1.02] ${
                    activeFeature === idx ? 'liquid-glass-elevated border-emerald-500/40 shadow-[0_0_30px_rgba(16,185,129,0.15)]' : 'liquid-glass-interactive border-white/5 hover:border-white/10'
                  }`}
                  onClick={() => setActiveFeature(idx)}
                >
                  <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/5">
                    {feat.icon}
                  </div>
                  <h3 className="text-base font-bold text-white">{feat.title}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed font-medium">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TECHNICAL SPECIFICATIONS TABLE */}
        <section className="py-24 px-6 bg-slate-950/40 border-t border-white/5 relative z-10">
          <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-4 text-right lg:text-left">
              <h3 className="text-2xl md:text-3xl font-black tracking-tight">{t.specifications.title}</h3>
              <p className="text-slate-400 text-xs leading-relaxed font-medium">{t.specifications.desc}</p>
            </div>

            <div className="lg:col-span-7 liquid-glass rounded-2xl overflow-hidden shadow-2xl p-2">
              <div className="divide-y divide-white/5">
                {t.specifications.items.map((spec, idx) => (
                  <div key={idx} className="flex justify-between items-center p-4 text-xs font-semibold">
                    <span className="text-slate-400 font-mono uppercase tracking-wider">{spec.name}</span>
                    <span className="text-slate-100">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ SECTION (GEO & Traditional SEO Citable Content) */}
        <section className="py-24 px-6 relative z-10 border-t border-white/5 bg-slate-950/20">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight">{t.faq.title}</h2>
              <p className="text-slate-400 text-sm md:text-base max-w-xl mx-auto font-medium">{t.faq.subtitle}</p>
            </div>

            <div className="space-y-6">
              {t.faq.items.map((item, idx) => (
                <div key={idx} className="p-6 rounded-2xl border border-white/5 bg-[#050E1C]/20 backdrop-blur-md hover:border-emerald-500/20 transition-all duration-300">
                  <h3 className="text-base font-bold text-white mb-2 flex gap-2">
                    <span className="text-emerald-400">؟</span>
                    {item.q}
                  </h3>
                  <p className="text-xs md:text-sm text-slate-400 leading-relaxed font-medium">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA PANEL */}
        <section className="py-32 relative overflow-hidden text-center bg-[#02040a] z-10 border-t border-white/5">
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/10 to-transparent opacity-20 pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 relative z-10 space-y-8">
            <div className="w-20 h-20 rounded-3xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 mx-auto border border-emerald-500/20">
              <ShieldCheck size={32} />
            </div>

            <h2 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight text-white max-w-3xl mx-auto">
              {isAr ? 'ابدأ رقمنة وتدقيق الأراضي الزراعية اليوم' : 'Secure Agricultural Claim Integrity Instantly'}
            </h2>
            
            <p className="text-slate-400 text-base md:text-lg font-medium max-w-2xl mx-auto leading-relaxed">
              {isAr 
                ? 'ادخل إلى مساحة العمل الرقمية لرسم حدود حقولك وبدء فحص الأقمار الصناعية فورياً.'
                : 'Access the CropLens interactive workspace to trace fields, compute NDVI index layers, and compile audit files.'
              }
            </p>

            <button
              onClick={() => navigate('/products/croplens/workspace')}
              className="px-12 py-5 rounded-2xl liquid-glass-button-primary text-white font-bold uppercase tracking-widest text-[12px]"
            >
              {isAr ? 'فتح مساحة عمل كروب لينس' : 'Launch CropLens Portal'}
            </button>
          </div>
        </section>

      </div>

      <footer className="py-20 bg-black/60 border-t border-white/5 text-center relative z-10">
        <p className="text-slate-600 text-xs font-mono tracking-widest uppercase">
          © 2026 VEXL STUDIO • {isAr ? 'فكسل كروب لينس' : 'VEXL CROPLENS'} • {isAr ? 'القاهرة' : 'CAIRO'}
        </p>
      </footer>
    </PageTransition>
  );
};

export default CropLensPage;
